import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";

export type TagOption = {
  id: string;
  primary: string;
};

interface TagSelectorProps {
  helpText: string;
  multiSelect?: boolean;
  allowCreate?: boolean;
  options: TagOption[];
  handleSelect?: (value: TagOption) => void;
  handleRemove?: (value: TagOption) => void;
  handleCreate?: (value: string) => void; // required if allowCreate is present
  defaultSelectedTags: TagOption[];
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  helpText,
  multiSelect,
  allowCreate,
  options,
  handleSelect,
  handleRemove,
  handleCreate,
  defaultSelectedTags = [],
}) => {
  const { isMobile } = useBreakpoint();

  const prevDefaultTagsRef = useRef<TagOption[]>(defaultSelectedTags);
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(defaultSelectedTags);
  const [searchText, setSearchText] = useState<string>("");

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const DROPDOWN_WIDTH = 400 * 0.8; // 400px is the width of the sidebar
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    // check the tags actually changed
    const prevIds = prevDefaultTagsRef.current
      .map((t) => t.id)
      .sort()
      .join(",");
    const currentIds = defaultSelectedTags
      .map((t) => t.id)
      .sort()
      .join(",");

    if (prevIds !== currentIds) {
      setSelectedTags(defaultSelectedTags);
      prevDefaultTagsRef.current = defaultSelectedTags;
    }
  }, [defaultSelectedTags]);

  // calculate dropdown position based on trigger element
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
        width: DROPDOWN_WIDTH,
      });
    }
  }, [isOpen, selectedTags]);

  // update position of the portal if the window changes size/scrolls
  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.right + window.scrollX - DROPDOWN_WIDTH,
          width: DROPDOWN_WIDTH,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, selectedTags]);

  // close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      const isOutsideTrigger = !triggerRef.current?.contains(target);
      const isOutsideDropdown = !dropdownRef.current?.contains(target);

      if (isOutsideTrigger && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    setSearchText("");
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelectTag = (tag: TagOption) => {
    const alreadyExists = selectedTags.some((t) => t.id === tag.id);
    if (alreadyExists) return;

    if (multiSelect) {
      setSelectedTags((prevTags) => {
        return [...prevTags, tag];
      });
    } else {
      setSelectedTags([tag]);
    }

    if (handleSelect) {
      handleSelect(tag);
    }
  };

  const handleRemoveTag = (tag: TagOption) => {
    setSelectedTags((prevTags) => prevTags.filter((t) => t.id !== tag.id));

    if (handleRemove) {
      handleRemove(tag);
    }
  };

  const filteredOptions = options.filter((option) => option.primary.toLowerCase().includes(searchText.toLowerCase()));

  const dropdown =
    isOpen &&
    createPortal(
      <>
        {isMobile && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsOpen(false)}
            onTouchStart={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}
        <div
          ref={dropdownRef}
          className={clsx(
            "fixed z-50 border border-border-subtle shadow-lg bg-bg-surface",
            isMobile ? "bottom-0 left-0 right-0 h-[40vh] rounded-t-md flex flex-col overflow-hidden" : "rounded-md",
          )}
          style={
            isMobile
              ? undefined
              : {
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                }
          }
        >
          <div className="flex flex-row flex-wrap gap-2 border-b border-border-subtle p-2">
            {selectedTags.length > 0 && (
              <>
                {selectedTags.map((tag) => (
                  <div key={tag.id} className="flex flex-row gap-1 items-center bg-bg-pressed rounded-md px-2 w-fit">
                    <Paragraph size="small" colorType="primary">
                      {tag.primary}
                    </Paragraph>

                    <XMarkIcon
                      className="text-text-secondary w-4 h-4 cursor-pointer"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  </div>
                ))}
              </>
            )}

            <input
              className="border-none outline-hidden focus:ring-0 p-0 text-text-primary text-sm"
              placeholder="Search for a tag"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="max-h-[30vh] overflow-auto">
            <div className="p-2">
              <Paragraph size="small">{helpText}</Paragraph>
            </div>

            <div className={clsx("px-2", isMobile && "flex-1 min-h-0 overflow-y-auto")}>
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  className="bg-bg-pressed rounded-md px-2 w-fit cursor-pointer mb-2"
                  onClick={() => {
                    handleSelectTag(option);
                  }}
                >
                  <Paragraph size="small" colorType="primary">
                    {option.primary}
                  </Paragraph>
                </div>
              ))}
            </div>
          </div>

          {allowCreate && handleCreate && searchText && (
            <div
              className="flex flex-row gap-2 p-2 hover:cursor-pointer hover:bg-bg-hovered"
              onClick={() => {
                handleCreate(searchText);
                setSearchText("");
              }}
            >
              <Paragraph size="small" colorType="primary">
                Create
              </Paragraph>
              <div className="flex flex-row gap-1 items-center bg-bg-pressed rounded-md px-2 w-fit">
                <Paragraph size="small" colorType="primary">
                  {searchText}
                </Paragraph>
              </div>
            </div>
          )}
        </div>
      </>,
      document.body,
    );

  return (
    <div>
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)} className="flex flex-row flex-wrap gap-2">
        {selectedTags.length > 0 ? (
          <>
            {selectedTags.map((tag) => (
              <div key={tag.id} className="flex flex-row bg-bg-pressed rounded-md px-2 w-fit">
                <Paragraph size="small" colorType="primary">
                  {tag.primary}
                </Paragraph>
              </div>
            ))}
          </>
        ) : (
          <div className="flex flex-row bg-bg-pressed rounded-md px-2 w-fit">
            <Paragraph size="small" colorType="primary">
              Select an option
            </Paragraph>
          </div>
        )}
      </div>

      {dropdown}
    </div>
  );
};
